import React, { useEffect, useRef, useState } from "react";
import { Button, Box, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import * as styles from "./Styles.js";
import SearchBoxComponent from "../../components/SearchBox/SearchBoxComponent.js";
import ApplyButton from "../../components/Buttons/ApplyButton.js";
import IBsReportRequest from "../../Endpoints/IBsReportRequest.js";
import TitledMultiSelector from "../../components/Selectors/TitledMultiSelector.js";
import TableComponent from "../../components/Table/TableComponent.js";
import { RemoveDashboardData } from "../../Tools/LocalStorage.js";
import {
  getLocalTimeZoneOffsetInSeconds,
  getTimestamps,
} from "../../Tools/DateTimeConvertions.js";
import DateSelector from "../../components/Selectors/DateSelector.js";
import TitledSelector from "../../components/Selectors/TitledSelector.js";
import IconComponent from "../../components/Icons/IconComponent.js";
import TableToolTip from "../../components/ToolTips/TableToolTip.js";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import LoadingSpinner from "../../components/Loaders/SpinLoader.js";
import {
  DATE_RANGE_OPTIONS,
  ROWS_PER_PAGE_OPTIONS,
  DEFAULT_ROWS_PER_PAGE,
  buildReportRow,
  computeNextIbSelection,
  fmt,
  getSummaryRowsFromReportRows,
  parseDateToMs,
} from "../../Tools/IbsReportUtils.js";

// This tab reuses the shared, DataGrid-based `TableComponent` (same one used
// elsewhere in the app), so it only ever needs ONE row per tree/IB - the
// already-aggregated summary totals (getSummaryRowsFromReportRows). The
// original design showed an expandable accordion with per-transaction child
// rows underneath each summary row, but MUI's DataGrid only supports that
// ("master-detail") pattern on the paid Pro/Premium tiers - the shared
// `TableComponent` here is built on the free Community DataGrid. That
// per-transaction detail is not lost though: it's exactly what the "Full
// Report" tab shows, in full, for the same filters.
const columns = [
  {
    field: "date",
    headerName: "Date",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1.1,
    minWidth: 160,
    renderCell: (params) => params.row.dateLabel,
  },
  { field: "ib_id", headerName: "IB ID", type: "number", flex: 0.6, minWidth: 90 },
  { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
  { field: "ib_email", headerName: "Email", flex: 1.3, minWidth: 200 },
  {
    field: "deposit",
    headerName: "Deposits",
    type: "number",
    flex: 0.8,
    minWidth: 110,
    renderCell: (params) => fmt(params.value),
  },
  {
    field: "withdraw",
    headerName: "Withdrawals",
    type: "number",
    flex: 0.9,
    minWidth: 120,
    renderCell: (params) => fmt(params.value),
  },
  {
    field: "payout",
    headerName: "Payouts",
    type: "number",
    flex: 0.8,
    minWidth: 110,
    renderCell: (params) => fmt(params.value),
  },
  {
    field: "wallet",
    headerName: "Wallet",
    type: "number",
    flex: 0.8,
    minWidth: 110,
    renderCell: (params) => fmt(params.value),
  },
  { field: "currency", headerName: "Currency", flex: 0.6, minWidth: 100 },
];

const DEFAULT_SORT_MODEL = [{ field: "date", sort: "desc" }];

function NoRowsOverlay() {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        color: "#334155",
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 32, color: "#334155" }} />
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>
        No results found.
      </Typography>
    </Box>
  );
}

// This is the original "My IBs" table/filters, ported almost verbatim into
// its own tab (the "Summarized Report" tab). Only the IB selector options
// (`ibOptions`/`individualEmails`/`ibOptionsLoading`) now come from the
// parent (`Tools/useIbOptions.js`) instead of being resolved here, so the
// list is shared with (and not refetched by) the "Full Report" tab.
function SummarizedReportTab({
  name,
  canUseIbSelector,
  ibOptions,
  individualEmails,
  ibOptionsLoading,
}) {
  const [dateSelect, setDateSelect] = useState("Today");
  const [showDataPickers, setShowDataPickers] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [brokerSelect, setBrokerSelect] = useState([]);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const didInitialFetch = useRef(false);

  const RequestIBsListData = async (params) => {
    setLoading(true);
    try {
      const dataResponse = await IBsReportRequest(params);
      if (dataResponse.status == 200) {
        const storedReports = dataResponse.data.data.reports;

        if (storedReports && Array.isArray(storedReports) && storedReports.length > 0) {
          const parsedRows = storedReports.map((report, index) => buildReportRow(report, index));
          const summaryRows = getSummaryRowsFromReportRows(parsedRows).map((row) => ({
            ...row,
            dateLabel: row.date,
            date: parseDateToMs(row.date),
          }));

          setRows(summaryRows);
          setFilteredRows(summaryRows);
        } else {
          setRows([]);
          setFilteredRows([]);
        }
      }
    } catch (error) {
      console.log(error);
      RemoveDashboardData();
    }

    setLoading(false);
  };

  // Initial load: runs once the shared IB selector options are ready. Ported
  // as-is from the original page: the very first request only sends the
  // real emails (`individualEmails`) - "All" is only ever included from
  // Apply onward, via `brokerSelect`.
  useEffect(() => {
    if (!canUseIbSelector || ibOptionsLoading || didInitialFetch.current) return;
    didInitialFetch.current = true;

    setBrokerSelect(["All", ...individualEmails]);
    setRows([]);
    setFilteredRows([]);

    if (!individualEmails.length) return;

    const datetime = getTimestamps("Today");
    RequestIBsListData({
      from: String(datetime.from),
      to: String(datetime.to),
      ib_email: individualEmails,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUseIbSelector, ibOptionsLoading, individualEmails]);

  const handleFilteredRows = (rowsevent) => {
    setFilteredRows(rowsevent);
  };

  const handleFromChange = (date) => setFromDate(date);
  const handleToChange = (date) => setToDate(date);

  const handleDownload = (dataArray) => {
    const exportRows = dataArray.map(({ id, hierarchy_id, lastTxnDate, date, dateLabel, ...rest }) => ({
      date: dateLabel,
      ...rest,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, ws, "IbsData");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fecha = new Date();
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, "0");
    const day = String(fecha.getDate()).padStart(2, "0");
    saveAs(blob, `${name}_Ibs_Summarized_${year}_${month}_${day}.xlsx`);
  };

  const handleDateSelector = (event) => {
    setDateSelect(event.target.value);
    setShowDataPickers(event.target.value === "Custom");
  };

  const handleBrokerIbSelector = (selectedValues) => {
    const allOptions = ibOptions.map((item) => item.value);
    setBrokerSelect((prev) => computeNextIbSelection(selectedValues, prev, allOptions));
  };

  const HandleOnClick = () => {
    if (dateSelect != "Custom") {
      const datetime = getTimestamps(dateSelect);
      RequestIBsListData({
        from: String(datetime.from),
        to: String(datetime.to),
        ib_email: brokerSelect,
      });
    } else if (fromDate != null || toDate != null) {
      RequestIBsListData({
        from: fromDate / 1000 + getLocalTimeZoneOffsetInSeconds(),
        to: toDate / 1000 + getLocalTimeZoneOffsetInSeconds() + 86400,
        ib_email: brokerSelect,
      });
    }
  };

  return (
    <Box>
      {loading ? <LoadingSpinner /> : <></>}

      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-end", flexWrap: "wrap", gap: 2 }}>
        <TitledSelector
          title="Period of time"
          value={dateSelect}
          handleChange={handleDateSelector}
          options={DATE_RANGE_OPTIONS}
        />

        {showDataPickers && (
          <>
            <DateSelector title="From" value={fromDate} handleChange={handleFromChange} />
            <DateSelector title="To" value={toDate} handleChange={handleToChange} />
          </>
        )}

        {canUseIbSelector ? (
          <TitledMultiSelector
            title="IB selector"
            value={brokerSelect}
            handleChange={handleBrokerIbSelector}
            options={ibOptions}
          />
        ) : null}

        <TableToolTip title="The selected time period only affects the calculation of Deposits, Withdrawals and Payouts. The Wallet will always be the current value of the IB">
          <Box>
            <ApplyButton HandleOnClick={HandleOnClick} disable={brokerSelect && brokerSelect.length === 0}>
              Apply <IconComponent icon="help" size="12px" />
            </ApplyButton>
          </Box>
        </TableToolTip>

        <Box sx={{ marginLeft: "auto", marginTop: "25px", display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            sx={styles.ExportButton}
            onClick={() => handleDownload(filteredRows)}
            disabled={filteredRows.length == 0}
          >
            <IconComponent icon="download" size="12px" />
            &nbsp;Export
          </Button>

          <SearchBoxComponent
            rows={rows}
            onFilteredRowsChange={handleFilteredRows}
            placeholder="Search by IB ID, name or email"
            filterFields={["ib_id", "name", "ib_email"]}
          />
        </Box>
      </Box>

      <Box sx={{ width: "100%", marginTop: 3 }}>
        <TableComponent
          rows={filteredRows}
          columns={columns}
          sortConfig={DEFAULT_SORT_MODEL}
          pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
          initialPageSize={DEFAULT_ROWS_PER_PAGE}
          slots={{ noRowsOverlay: NoRowsOverlay }}
        />
      </Box>
    </Box>
  );
}

export default SummarizedReportTab;